<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { _ } from 'svelte-i18n';

  import { AppState } from '../../etc/stores';
  import { replaceVariables } from '../../i18n';
  import PaymentCodeIcon from '../icons/PaymentCodeIcon.svelte';
  import Heading from '../typography/Heading.svelte';
  import Text from '../typography/Text.svelte';
  import CopyCodeV2 from './CopyCodeV2.svelte';
  import Button from '../form/Button.svelte';
  import RewardCoinIcon from '../icons/RewardCoinIcon.svelte';
  import Analytics from '../../services/analytics';
  import Alert from '../alert/Alert.svelte';
  import flagKeys from '../../etc/flagKeys';
  import ExpiringCountdown from '../expiring-countdown/ExpiringCountdown.svelte';

  export let redemptionData;

  export let value: string;

  export let loading: boolean;

  export let onCopy;

  let step = 0;

  let category = 'fast-redemption-state';

  $: expirableCoinsIsActive = $AppState.featureFlags.includes(flagKeys.EXPIRABLE_COINS);

  const dispatch = createEventDispatcher();

  const t = (text: string, values: any = {}) => {
    return replaceVariables($_(`fast-redemption.${text}`), values);
  };

  const redeem = () => {
    dispatch('redeem');
  };

  const convert = () => {
    dispatch('convert');
  };

  const convertNext = () => {
    step++;
    Analytics.logEvent(`click-convert-coins`, category);
  };

  const convertCancel = () => {
    step--;
    Analytics.logEvent(`click-convert-cancel`, category);
  };

  const copyCode = () => {
    Analytics.logEvent(`click-copy-code`, category);
  };
</script>

<div class="twism-redemption-action">
  <div class="twism-redemption-action__header">
    {#if redemptionData.redeemType === 'convert'}
      <RewardCoinIcon />
    {:else}
      <PaymentCodeIcon />
    {/if}
    <div>
      <Heading type="h2">
        {t(`title.${redemptionData.redeemType}`, {
          value,
          businessName: $AppState.business.name,
          coins: redemptionData.coins,
          firstName: redemptionData.name
        })}
      </Heading>
      {#if redemptionData.redeemType === 'convert' || redemptionData.redeemType === 'coupon'}
        <Text type="medium" color="#7A89B0">
          {t(`description.${redemptionData.redeemType}`)}
        </Text>
      {/if}
    </div>
  </div>
  {#if redemptionData.redeemType === 'coupon'}
    <div class="twism-redemption-action__container-btns">
      {#if expirableCoinsIsActive && redemptionData?.expiresAt}
      <Alert align="center">
        <ExpiringCountdown
          showExpiredLabel
          emitExpiredEventOnFirstTime
          expiresAt={redemptionData.expiresAt}
        />
      </Alert>
      {/if}
      <CopyCodeV2
        class="twism-redemption-action__code-container"
        code={redemptionData.code}
        on:copy={copyCode}
        bind:onCopy
      />
    </div>
  {:else if redemptionData.redeemType === 'convert'}
    {#if step === 0}
      <Button class="twism-redemption-action__cta" on:click={convertNext}>
        {t('cta.convert')}
      </Button>
    {:else}
      <div class="twism-redemption-action__disclaimer">
        <Text type="medium">
          {t(
            `disclaimer.${
              redemptionData.coins === Number($AppState.customer.wallet.coins)
                ? 'full-balance'
                : 'equivalent-balance'
            }`
          )}
        </Text>
      </div>
      <div class="twism-redemption-action__buttons">
        <Button schema="secondary" on:click={convertCancel}>
          <Text color={$AppState.theme.colors.textLighter} bold>
            {t('btn-cancel')}
          </Text>
        </Button>
        <Button on:click={convert} {loading}>
          <Text color={$AppState.theme.colors.textAboveBackground} bold>
            {t('btn-confirm')}
          </Text>
        </Button>
      </div>
    {/if}
  {:else}
    <Button class="twism-redemption-action__cta" on:click={redeem} {loading}>
      {t('cta.claim')}
    </Button>
  {/if}
</div>

<style lang="scss">
  .twism-redemption-action {
    background: #ffffff;
    border: 1px solid var(--twism-theme-primary500);
    box-shadow: 0px 16px 40px rgba(112, 144, 176, 0.2);
    border-radius: 12px;
    box-sizing: border-box;
    padding: 24px 14px;

    :global(.twm-expiring-countdown) {
      font-weight: 500;
    }

    &__container-btns {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    &__header {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      margin-bottom: 16px;

      :global(svg) {
        min-width: 32px;
      }

      :global(h2) {
        margin-bottom: 4px;
      }
    }

    &__disclaimer {
      width: 100%;
      padding: 16px;
      background: var(--twism-theme-primary600);
      border-radius: 8px;
      margin-bottom: 16px;
      box-sizing: border-box;
    }

    &__buttons {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
    }

    :global(.twism-redemption-action__cta) {
      font-weight: 600;
      font-size: 16px;
      line-height: 24px;
      letter-spacing: -0.01em;
    }
  }
</style>
